import { useState } from 'react';
import { CasYesNoSelect } from 'src/compass/application';
import { IGeneralObjectType } from 'src/compass/site/util';

export const SERVICE_OWNER_FILTER = 'isRMCOfRecord';

/*
  "Is Service Owner" for the Customers page's tabs (Customers, Touchpoints, Unassigned).
  Yes = customers the RMC filter's employee is the Service Owner of (the entity's
  RMCOfRecordEmployeeId) - one RMC per customer, so a shared customer isn't worked or messaged
  twice. It means nothing without an RMC, so it goes right after the RMC filter and is shown only
  while RMC has a value (`visibleWhen`). In the Customers tab's More drawer that is the staged RMC,
  so picking one there brings it up before Apply. While RMC is empty (All) it holds Yes - sent, but
  the API ignores it without an RMC - so it is Yes whenever it appears, including when RMC goes
  from All to a person.

  CasTable reads each filter's initialValue only at mount, and no filter can set another's value.
  So the tab tracks every filter's value here and remounts the table (the returned key) when RMC
  switches between empty and a person, re-seeding every filter. Paging and sorting restart then.

  Returns a function taking the tab's filter list (with the RMC filter named rmcFilterName in
  it) and giving back the filters to hand CasTable, the table's key, and the Is Service Owner
  value in effect (null while it isn't offered).
*/
export default function useServiceOwnerFilter(rmcFilterName: string) {
  // Every filter's value once it has been changed; a filter missing from here is still at its
  // initialValue.
  const [filterValues, setFilterValues] = useState<IGeneralObjectType>({});

  const valueOf = (name: string, initialValue: any) =>
    name in filterValues ? filterValues[name] : initialValue;

  return (filters: IGeneralObjectType[]) => {
    const rmcInitialValue = filters.find((f) => f.name === rmcFilterName)?.initialValue;
    const rmcEmployeeId = valueOf(rmcFilterName, rmcInitialValue);
    const isServiceOwner = rmcEmployeeId != null ? valueOf(SERVICE_OWNER_FILTER, true) : null;

    const record = (name: string, value: any) =>
      setFilterValues((prev) => {
        const next = { ...prev, [name]: value };
        // RMC empty puts Is Service Owner back to Yes, ready for the next person picked.
        const rmc = rmcFilterName in next ? next[rmcFilterName] : rmcInitialValue;
        return rmc == null ? { ...next, [SERVICE_OWNER_FILTER]: true } : next;
      });

    const tracked = filters.flatMap((filter) => {
      const seeded = {
        ...filter,
        initialValue: valueOf(filter.name, filter.initialValue),
        onChange: (value: any) => {
          filter.onChange?.(value);
          record(filter.name, value);
        },
      };
      if (filter.name !== rmcFilterName) return [seeded];

      return [
        seeded,
        {
          type: 'component',
          name: SERVICE_OWNER_FILTER,
          label: 'Is Service Owner',
          // On the toolbar or in the drawer, wherever the RMC filter is.
          isPrimaryFilter: filter.isPrimaryFilter,
          visibleWhen: (values: IGeneralObjectType) => values[rmcFilterName] != null,
          initialValue: valueOf(SERVICE_OWNER_FILTER, true),
          onChange: (value: any) => record(SERVICE_OWNER_FILTER, value),
          includeAllOption: true,
          component: CasYesNoSelect,
        },
      ];
    });

    return {
      filters: tracked,
      key: rmcEmployeeId == null ? 'rmcAll' : 'rmcPicked',
      isServiceOwner,
    };
  };
}
